import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	getAllCoursesOptions,
	getSchoolsOptions,
} from "@/lib/client/@tanstack/react-query.gen";
import type {
	Course,
	ParentWithStudentsDto,
	SmsEstimateResponse,
} from "@/lib/client/types.gen";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { MessageSquare, Send, Calculator } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { DataTable, DataTableColumnHeader, DataTableToolbar } from "@/components/data-table";
import { normalizeString } from "@/lib/utils";

export const Route = createFileRoute("/admin/sms/")({
	component: SmsManagementPage,
	validateSearch: (search: Record<string, unknown>) => {
		return {
			courseId: (search.courseId as string) || undefined,
			parentIds: (search.parentIds as string[]) || undefined,
		};
	},
});

function SmsManagementPage() {
	const queryClient = useQueryClient();
	const search = Route.useSearch();
	const [selectedSchool, setSelectedSchool] = useState<string | undefined>(undefined);
	const [selectedCourseId, setSelectedCourseId] = useState<string | undefined>(
		search.courseId || undefined
	);
	const [selectedDate, setSelectedDate] = useState<string>("");
	const [paymentOnHoldOnly, setPaymentOnHoldOnly] = useState(false);
	const [parents, setParents] = useState<ParentWithStudentsDto[]>([]);
	const [selectedParentIds, setSelectedParentIds] = useState<Set<string>>(
		new Set()
	);
	const [message, setMessage] = useState("");
	const [estimate, setEstimate] = useState<SmsEstimateResponse | null>(null);

	const { data: schoolsData } = useQuery(getSchoolsOptions());
	const schools =
		(schoolsData as any as Array<{ value: string; label: string }>) || [];

	const { data: coursesData } = useQuery(getAllCoursesOptions());
	const allCourses = (coursesData as any as Course[]) || [];

	const filterParentsMutation = useMutation({
		mutationFn: async (filters: any) => {
			const response = await fetch(
				`${import.meta.env.VITE_API_URL}/api/admin/sms/filter-parents`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
					body: JSON.stringify(filters),
				}
			);
			if (!response.ok) throw new Error("Erreur lors du filtrage");
			return response.json();
		},
		onSuccess: (data: ParentWithStudentsDto[]) => {
			setParents(data);
			if (search.parentIds) {
				setSelectedParentIds(new Set(search.parentIds));
			}
			toast.success(`${data.length} parent(s) trouvé(s)`);
		},
		onError: () => {
			toast.error("Erreur lors du filtrage des parents");
		},
	});

	const estimateMutation = useMutation({
		mutationFn: async (request: {
			recipients: string[];
			message: string;
		}) => {
			const response = await fetch(
				`${import.meta.env.VITE_API_URL}/api/admin/sms/estimate`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
					body: JSON.stringify(request),
				}
			);
			if (!response.ok) throw new Error("Erreur lors de l'estimation");
			return response.json();
		},
		onSuccess: (data: SmsEstimateResponse) => {
			setEstimate(data);
			toast.success("Estimation calculée");
		},
		onError: () => {
			toast.error("Erreur lors de l'estimation");
		},
	});

	const sendSmsMutation = useMutation({
		mutationFn: async (request: { parentIds: string[]; message: string }) => {
			const response = await fetch(
				`${import.meta.env.VITE_API_URL}/api/admin/sms/send`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
					body: JSON.stringify(request),
				}
			);
			if (!response.ok) throw new Error("Erreur lors de l'envoi");
			return response.json();
		},
		onSuccess: (data: any) => {
			toast.success(
				`SMS envoyés: ${data.successCount} réussis, ${data.failureCount} échecs`
			);
			setMessage("");
			setEstimate(null);
			setSelectedParentIds(new Set());
		},
		onError: () => {
			toast.error("Erreur lors de l'envoi des SMS");
		},
	});

	const handleFilterParents = (customFilters?: any) => {
		const filters = customFilters || {
			courseId: selectedCourseId || undefined,
			school: selectedSchool || undefined,
			date: selectedDate || undefined,
			paymentOnHold: paymentOnHoldOnly ? true : undefined,
			parentIds: undefined,
		};
		filterParentsMutation.mutate(filters);
	};

	useEffect(() => {
		if (search.courseId) {
			setSelectedCourseId(search.courseId);
			handleFilterParents({ courseId: search.courseId });
		} else if (search.parentIds && search.parentIds.length > 0) {
			handleFilterParents({ parentIds: search.parentIds });
		} else {
			handleFilterParents({});
		}
	}, []);

	const handleEstimate = () => {
		const selectedParents = parents.filter((p) =>
			selectedParentIds.has(p.id)
		);
		const recipients = selectedParents.map((p) => p.phone);
		estimateMutation.mutate({ recipients, message });
	};

	const handleSendSms = () => {
		if (
			!window.confirm(
				`Confirmer l'envoi de ${selectedParentIds.size} SMS (coût estimé: ${estimate?.estimatedCost?.toFixed(2)} €)?`
			)
		) {
			return;
		}
		sendSmsMutation.mutate({
			parentIds: Array.from(selectedParentIds),
			message,
		});
	};

	const toggleParent = (parentId: string) => {
		const newSet = new Set(selectedParentIds);
		if (newSet.has(parentId)) {
			newSet.delete(parentId);
		} else {
			newSet.add(parentId);
		}
		setSelectedParentIds(newSet);
	};

	const toggleSelectAll = () => {
		if (selectedParentIds.size === parents.length) {
			setSelectedParentIds(new Set());
		} else {
			setSelectedParentIds(new Set(parents.map((p) => p.id)));
		}
	};

	const getStudentCourses = (student: any) => {
		if (!student.subjects || !allCourses) return [];

		return allCourses.filter(course =>
			student.subjects?.includes(course.subject) &&
			course.level === student.level &&
			course.school === student.school &&
			student.timeSlots?.some((slot: string) => slot === course.timeSlot)
		);
	};

	const columns = useMemo<ColumnDef<ParentWithStudentsDto>[]>(
		() => [
			{
				id: "select",
				header: () => (
					<Checkbox
						checked={
							parents.length > 0 &&
							selectedParentIds.size === parents.length
						}
						onCheckedChange={toggleSelectAll}
					/>
				),
				cell: ({ row }) => (
					<Checkbox
						checked={selectedParentIds.has(row.original.id)}
						onCheckedChange={() => toggleParent(row.original.id)}
					/>
				),
				enableSorting: false,
			},
			{
				id: "parent",
				accessorFn: (row) => row.lastName + " " + row.firstName,
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Parent" />
				),
				cell: ({ row }) => (
					<div>
						<div className="font-medium">
							{row.original.firstName} {row.original.lastName}
						</div>
						<div className="text-sm text-gray-500">{row.original.phone}</div>
					</div>
				),
				filterFn: (row, columnId, filterValue: string) => {
					const searchValue = normalizeString(filterValue);
					const fullName = normalizeString(
						`${row.original.firstName} ${row.original.lastName}`
					);
					const reverseName = normalizeString(
						`${row.original.lastName} ${row.original.firstName}`
					);
					const phone = normalizeString(row.original.phone || "");

					const studentsMatch = row.original.students?.some(student => {
						const studentFullName = normalizeString(
							`${student.firstName} ${student.lastName}`
						);
						const studentReverseName = normalizeString(
							`${student.lastName} ${student.firstName}`
						);
						const className = normalizeString(student.className || "");
						return studentFullName.includes(searchValue) ||
							studentReverseName.includes(searchValue) ||
							className.includes(searchValue);
					}) || false;

					return fullName.includes(searchValue) ||
						reverseName.includes(searchValue) ||
						phone.includes(searchValue) ||
						studentsMatch;
				},
				sortDescFirst: false,
				meta: { label: "Parent" },
			},
			{
				id: "students",
				accessorFn: (row) =>
					row.students?.map(s => `${s.firstName} ${s.lastName}`).join(", ") || "",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Enfants" />
				),
				cell: ({ row }) => (
					<div className="space-y-1">
						{row.original.students?.map((student, idx) => (
							<div key={idx} className="text-sm">
								<span className="font-medium">
									{student.firstName} {student.lastName}
								</span>
								{student.className && (
									<span className="text-gray-500 ml-2">
										({student.className})
									</span>
								)}
							</div>
						))}
					</div>
				),
				filterFn: (row, columnId, filterValue: string) => {
					const searchValue = normalizeString(filterValue);
					return row.original.students?.some(student => {
						const fullName = normalizeString(
							`${student.firstName} ${student.lastName}`
						);
						const className = normalizeString(student.className || "");
						return fullName.includes(searchValue) || className.includes(searchValue);
					}) || false;
				},
				meta: { label: "Enfants" },
			},
			{
				id: "courses",
				accessorFn: (row) => {
					const courses = row.students?.flatMap(student =>
						getStudentCourses(student).map(c => c.subject)
					) || [];
					return courses.join(", ");
				},
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Cours" />
				),
				cell: ({ row }) => (
					<div className="space-y-1">
						{row.original.students?.map((student, idx) => {
							const courses = getStudentCourses(student);
							return courses.length > 0 ? (
								<div key={idx} className="text-sm">
									{courses.map(course => course.subject).join(", ")}
								</div>
							) : null;
						})}
					</div>
				),
				meta: { label: "Cours" },
			},
			{
				id: "teachers",
				accessorFn: (row) => {
					const teachers = row.students?.flatMap(student =>
						getStudentCourses(student)
							.map(c => c.teacher ? `${c.teacher.firstName} ${c.teacher.lastName}` : "")
							.filter(Boolean)
					) || [];
					return [...new Set(teachers)].join(", ");
				},
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Professeurs" />
				),
				cell: ({ row }) => {
					const allTeachers = new Set<string>();
					row.original.students?.forEach(student => {
						getStudentCourses(student).forEach(course => {
							if (course.teacher) {
								allTeachers.add(
									`${course.teacher.firstName} ${course.teacher.lastName}`
								);
							}
						});
					});
					return (
						<div className="text-sm">
							{allTeachers.size > 0 ? (
								Array.from(allTeachers).map((teacher, idx) => (
									<div key={idx}>{teacher}</div>
								))
							) : (
								<span className="text-gray-400">-</span>
							)}
						</div>
					);
				},
				meta: { label: "Professeurs" },
			},
		],
		[parents, selectedParentIds, allCourses]
	);

	return (
		<div className="container mx-auto p-6">
			<div className="mb-6">
				<h1 className="text-3xl font-bold">Envoi de SMS groupés</h1>
				<p className="text-gray-500">
					Filtrez et sélectionnez les parents pour envoyer des SMS
				</p>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				<div className="lg:col-span-2 space-y-6">
					<Card>
						<CardHeader>
							<CardTitle>Filtres</CardTitle>
							<CardDescription>
								Sélectionnez les critères pour filtrer les parents
							</CardDescription>
						</CardHeader>
						<CardContent>
							<div className="grid grid-cols-2 gap-4">
								<div>
									<Label>Cours</Label>
									<div className="flex gap-2">
										<Select
											value={selectedCourseId}
											onValueChange={setSelectedCourseId}
										>
											<SelectTrigger>
												<SelectValue placeholder="Tous les cours" />
											</SelectTrigger>
											<SelectContent>
												{allCourses.map((course) => (
													<SelectItem key={course.id} value={course.id}>
														{course.subject} - {course.level} - {course.school}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										{selectedCourseId && (
											<Button
												variant="outline"
												size="sm"
												onClick={() => setSelectedCourseId(undefined)}
											>
												Effacer
											</Button>
										)}
									</div>
								</div>
								<div>
									<Label>École</Label>
									<div className="flex gap-2">
										<Select value={selectedSchool} onValueChange={setSelectedSchool}>
											<SelectTrigger>
												<SelectValue placeholder="Toutes les écoles" />
											</SelectTrigger>
											<SelectContent>
												{schools.map((school) => (
													<SelectItem key={school.value} value={school.value}>
														{school.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										{selectedSchool && (
											<Button
												variant="outline"
												size="sm"
												onClick={() => setSelectedSchool(undefined)}
											>
												Effacer
											</Button>
										)}
									</div>
								</div>
								<div>
									<Label>Date</Label>
									<div className="flex gap-2">
										<Input
											type="date"
											value={selectedDate}
											onChange={(e) => setSelectedDate(e.target.value)}
										/>
										{selectedDate && (
											<Button
												variant="outline"
												size="sm"
												onClick={() => setSelectedDate("")}
											>
												Effacer
											</Button>
										)}
									</div>
								</div>
								<div className="col-span-2 flex items-center gap-2">
									<Checkbox
										checked={paymentOnHoldOnly}
										onCheckedChange={(checked) =>
											setPaymentOnHoldOnly(checked === true)
										}
									/>
									<Label>Paiements en pause uniquement</Label>
								</div>
							</div>
							<div className="flex gap-2 mt-4">
								<Button onClick={() => handleFilterParents()}>
									Appliquer les filtres
								</Button>
								<Button
									variant="outline"
									onClick={() => {
										setSelectedCourseId(undefined);
										setSelectedSchool(undefined);
										setSelectedDate("");
										setPaymentOnHoldOnly(false);
										setParents([]);
										setSelectedParentIds(new Set());
									}}
								>
									Réinitialiser
								</Button>
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle>Parents ({parents.length})</CardTitle>
							<CardDescription>
								{selectedParentIds.size} parent(s) sélectionné(s)
							</CardDescription>
						</CardHeader>
						<CardContent>
							<DataTable
								columns={columns}
								data={parents}
								emptyMessage="Aucun parent trouvé"
								toolbar={(table) => (
									<DataTableToolbar table={table} showExportExcel exportFilename="sms">
										<Input
											placeholder="Rechercher un parent, enfant, téléphone..."
											value={(table.getColumn("parent")?.getFilterValue() as string) ?? ""}
											onChange={(e) =>
												table.getColumn("parent")?.setFilterValue(e.target.value)
											}
											className="h-8 w-[350px]"
										/>
									</DataTableToolbar>
								)}
							/>
						</CardContent>
					</Card>
				</div>

				<div className="lg:col-span-1">
					<Card className="sticky top-6">
						<CardHeader>
							<CardTitle>Message SMS</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div>
								<Label>Message</Label>
								<Textarea
									value={message}
									onChange={(e) => setMessage(e.target.value)}
									placeholder="Votre message..."
									rows={6}
								/>
								<p className="text-sm text-gray-500 mt-1">
									{message.length} caractères ({Math.ceil(message.length / 160)}{" "}
									SMS)
								</p>
							</div>

							{estimate && (
								<Alert>
									<AlertTitle>Estimation</AlertTitle>
									<AlertDescription>
										<div>Contacts: {estimate.contactCount}</div>
										<div>SMS: {estimate.smsCount}</div>
										<div className="font-bold">
											Coût estimé: {estimate.estimatedCost?.toFixed(2)} €
										</div>
									</AlertDescription>
								</Alert>
							)}

							<div className="space-y-2">
								<Button
									variant="outline"
									className="w-full"
									onClick={handleEstimate}
									disabled={selectedParentIds.size === 0 || !message}
								>
									<Calculator className="mr-2 h-4 w-4" />
									Estimer le coût
								</Button>

								<Button
									className="w-full"
									onClick={handleSendSms}
									disabled={!estimate || selectedParentIds.size === 0 || !message}
								>
									<Send className="mr-2 h-4 w-4" />
									Envoyer ({selectedParentIds.size} parent
									{selectedParentIds.size > 1 ? "s" : ""})
								</Button>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	);
}
